
import React from 'react';
import { Badge } from "@/components/ui/badge";
import { PieChart, Users } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { formatCurrency, getPercentage, getCategoryColor, getCategoryIcon } from "../ai-advisor/utils/costAnalysisUtils";

interface CostBreakdownSectionProps {
  breakdown: Array<{
    category: string;
    amount: number;
    description: string;
    details: string;
  }>;
  totalCost: number;
  projectTaskCostSummary: Array<{
    sn: number;
    taskName: string;
    totalResourceCost: number;
    percentageOfTotalCost: number;
  }>;
}

const CostBreakdownSection = ({ breakdown, totalCost, projectTaskCostSummary }: CostBreakdownSectionProps) => {
  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-primary h-[380px]">
      <CardHeader className="pb-0">
        <CardTitle className="text-white text-lg flex items-center gap-2 py-2">
          Cost Breakdown by Role and Project Task
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 p-3">
        {/* Role Cards with Minimized Spacing */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {breakdown.map((item, index) => {
            const colors = [
              { bg: 'bg-purple-500/10', border: 'border-purple-500/20', text: 'text-purple-200', badge: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
              { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-200', badge: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
              { bg: 'bg-green-500/10', border: 'border-green-500/20', text: 'text-green-200', badge: 'bg-green-500/20 text-green-300 border-green-500/30' },
              { bg: 'bg-orange-500/10', border: 'border-orange-500/20', text: 'text-orange-200', badge: 'bg-orange-500/20 text-orange-300 border-orange-500/30' }
            ];
            const color = colors[index] || colors[0];
            const percentage = getPercentage(item.amount, totalCost);
            
            return (
              <div key={index} className={`${color.bg} p-2 rounded-lg border ${color.border} flex flex-col justify-between`}>
                <div className="flex items-center justify-between mb-0">
                  <span className={`${color.text} font-medium text-xs`}>{item.category}</span>
                  <Badge className={`${color.badge} text-xs`}>
                    {percentage}%
                  </Badge>
                </div>
                <p className="text-white text-sm font-semibold">{formatCurrency(item.amount)}</p>
              </div>
            );
          })}
        </div>

        {/* Project Task Cost Summary Cards */}
        <div className="h-48 overflow-y-auto border border-primary/20 rounded-lg p-2 bg-black/20">
          <div className="space-y-1.5">
            {projectTaskCostSummary.map((task, index) => {
              const colors = [
                { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-200', badge: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30' },
                { bg: 'bg-pink-500/10', border: 'border-pink-500/20', text: 'text-pink-200', badge: 'bg-pink-500/20 text-pink-300 border-pink-500/30' },
                { bg: 'bg-yellow-500/10', border: 'border-yellow-500/20', text: 'text-yellow-200', badge: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
                { bg: 'bg-indigo-500/10', border: 'border-indigo-500/20', text: 'text-indigo-200', badge: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
                { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-200', badge: 'bg-red-500/20 text-red-300 border-red-500/30' },
                { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-200', badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' }
              ];
              const color = colors[index % colors.length];
              
              return (
                <div key={task.sn} className={`${color.bg} p-2 rounded-lg border ${color.border} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <span className={`${color.text} font-medium text-xs`}>{task.sn}</span>
                    <span className={`${color.text} font-medium text-xs truncate max-w-100`}>{task.taskName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-xs font-semibold">{formatCurrency(task.totalResourceCost)}</span>
                    <Badge className={`${color.badge} text-xs`}>
                      {task.percentageOfTotalCost}%
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CostBreakdownSection;
